import React from "react";
import { Button, Col, Modal, Row, Select, Typography, Descriptions } from "antd";
import { useTranslation } from "react-i18next";
import type { CompliancePassport } from "../types";

type Props = {
  versions: CompliancePassport[];
};

function summarize(p: CompliancePassport | undefined) {
  if (!p) return null;
  const j = p.passport_json || {};
  const risk = (j.risk_assessment as { risk_score?: number } | undefined)?.risk_score;
  const commodity = j.commodity;
  const tr = (j.traceability_summary || j.traceability) as Record<string, unknown> | undefined;
  return {
    version: p.version,
    generatedAt: p.generated_at,
    commodity,
    risk,
    supplier: tr?.supplier_name,
    destination: tr?.destination_country,
    contract: tr?.contract_reference,
  };
}

export function PassportVersionCompare({ versions }: Props) {
  const { t } = useTranslation();
  const sorted = React.useMemo(
    () => [...versions].sort((a, b) => b.version - a.version),
    [versions]
  );
  const [open, setOpen] = React.useState(false);
  const [left, setLeft] = React.useState<number | undefined>();
  const [right, setRight] = React.useState<number | undefined>();

  React.useEffect(() => {
    if (sorted.length >= 2) {
      setLeft(sorted[1].version);
      setRight(sorted[0].version);
    }
  }, [sorted]);

  if (sorted.length < 2) return null;

  const leftP = summarize(sorted.find((v) => v.version === left));
  const rightP = summarize(sorted.find((v) => v.version === right));
  const options = sorted.map((v) => ({
    value: v.version,
    label: `v${v.version}`,
  }));

  const renderCol = (summary: ReturnType<typeof summarize>, title: string) => (
    <Col span={12}>
      <Typography.Title level={5}>{title}</Typography.Title>
      {summary ? (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={t("compliance.passport.version")}>
            v{summary.version}
          </Descriptions.Item>
          <Descriptions.Item label={t("compliance.passport.generatedAt")}>
            {new Date(summary.generatedAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={t("compliance.passport.commodity")}>
            {String(summary.commodity ?? t("common.dash"))}
          </Descriptions.Item>
          <Descriptions.Item label={t("compliance.passport.riskScore")}>
            {summary.risk != null ? String(summary.risk) : t("common.dash")}
          </Descriptions.Item>
          <Descriptions.Item label={t("compliance.columns.supplier")}>
            {String(summary.supplier ?? t("common.dash"))}
          </Descriptions.Item>
          <Descriptions.Item label={t("compliance.columns.destination")}>
            {String(summary.destination ?? t("common.dash"))}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <Typography.Text type="secondary">{t("common.dash")}</Typography.Text>
      )}
    </Col>
  );

  return (
    <>
      <Button data-testid="passport-compare-open" onClick={() => setOpen(true)}>
        {t("compliance.passport.compareVersions")}
      </Button>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
        width={900}
        title={t("compliance.passport.compareTitle")}
        destroyOnHidden
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Select
              style={{ width: "100%" }}
              options={options}
              value={left}
              onChange={setLeft}
              aria-label={t("compliance.passport.compareLeft")}
            />
          </Col>
          <Col span={12}>
            <Select
              style={{ width: "100%" }}
              options={options}
              value={right}
              onChange={setRight}
              aria-label={t("compliance.passport.compareRight")}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          {renderCol(leftP, t("compliance.passport.compareLeft"))}
          {renderCol(rightP, t("compliance.passport.compareRight"))}
        </Row>
      </Modal>
    </>
  );
}
